import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Download, Map, Trash2, AlertCircle, FileText, Eye, Image as ImageIcon, Camera, Upload, CheckCircle, XCircle, Clock, Navigation, Radio, Stamp } from 'lucide-react';
import KmRanking from '@/components/KmRanking';
import FreeModeProgress from '@/components/FreeModeProgress';
import { toast } from 'sonner';
import WaypointTracker from '@/components/WaypointTracker';

export default function MyRegistrations({ user }) {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [currentEventTitle, setCurrentEventTitle] = useState('');
  
  // Tracking photos state
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingRegistration, setTrackingRegistration] = useState(null);
  const [trackingPhotos, setTrackingPhotos] = useState([]);
  const [loadingTrackingPhotos, setLoadingTrackingPhotos] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [newPhotoDescription, setNewPhotoDescription] = useState('');
  const [newPhotoFile, setNewPhotoFile] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const fetchRegistrations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/registrations/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegistrations(response.data);
    } catch (error) {
      console.error('Error loading registrations:', error);
      // Only show error for actual server errors (5xx)
      if (error.response && error.response.status >= 500) {
        toast.error('Error al cargar inscripciones. Por favor, recarga la página.', {
          duration: 5000
        });
      }
      // For other errors, fail silently and let the empty state handle it
    } finally {
      setLoading(false);
    }
  };

  // Tracking photos functions
  const openTrackingModal = async (registration) => {
    setTrackingRegistration(registration);
    setShowTrackingModal(true);
    setNewPhotoDescription('');
    setNewPhotoFile(null);
    await fetchTrackingPhotos(registration.id);
  };

  const fetchTrackingPhotos = async (registrationId) => {
    setLoadingTrackingPhotos(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/registrations/${registrationId}/tracking-photos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrackingPhotos(response.data);
    } catch (error) {
      console.error('Error loading tracking photos:', error);
      toast.error('Error al cargar fotos de seguimiento');
    } finally {
      setLoadingTrackingPhotos(false);
    }
  };

  const handleUploadTrackingPhoto = async () => {
    if (!newPhotoFile || !trackingRegistration) return;
    
    setUploadingPhoto(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('photo', newPhotoFile);
      if (newPhotoDescription) {
        formData.append('description', newPhotoDescription);
      }
      
      await axios.post(
        `${API}/registrations/${trackingRegistration.id}/tracking-photos`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('Foto de seguimiento subida correctamente');
      setNewPhotoFile(null);
      setNewPhotoDescription('');
      await fetchTrackingPhotos(trackingRegistration.id);
    } catch (error) {
      console.error('Error uploading tracking photo:', error);
      toast.error(error.response?.data?.detail || 'Error al subir la foto');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeleteTrackingPhoto = async (photoId) => {
    if (!confirm('¿Estás seguro de eliminar esta foto?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/registrations/${trackingRegistration.id}/tracking-photos/${photoId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Foto eliminada');
      await fetchTrackingPhotos(trackingRegistration.id);
    } catch (error) {
      console.error('Error deleting tracking photo:', error);
      toast.error('Error al eliminar la foto');
    }
  };

  const canUploadTrackingPhotos = (event) => {
    if (!event?.requires_tracking) return false;
    
    const now = new Date();
    const startDate = new Date(event.start_date);
    const endDate = new Date(event.end_date);
    endDate.setDate(endDate.getDate() + 7); // 7 days after event end
    
    return now >= startDate && now <= endDate;
  };

  const getTrackingStatusInfo = (event) => {
    if (!event?.requires_tracking) return null;
    
    const now = new Date();
    const startDate = new Date(event.start_date);
    const endDate = new Date(event.end_date);
    const deadline = new Date(endDate);
    deadline.setDate(deadline.getDate() + 7);
    
    if (now < startDate) {
      return { status: 'pending', message: `Disponible desde ${event.start_date}`, canUpload: false };
    } else if (now <= deadline) {
      const daysLeft = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
      return { status: 'active', message: `${daysLeft} días restantes`, canUpload: true };
    } else {
      return { status: 'expired', message: 'Plazo expirado', canUpload: false };
    }
  };

  const handleDownloadGPX = async (eventId, eventTitle) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/gpx/${eventId}/download`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${eventTitle}.gpx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Archivo GPX descargado');
    } catch (error) {
      console.error('Error downloading GPX:', error);
      toast.error('Error al descargar GPX');
    }
  };

  const handleDownloadPDF = async (pdfPath, eventTitle) => {
    try {
      const token = localStorage.getItem('token');
      // Handle Cloudinary URLs (full URLs) vs local paths
      const url = pdfPath.startsWith('http') ? pdfPath : `${API}${pdfPath}`;
      const response = await axios.get(url, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `${eventTitle}_info.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success('PDF descargado correctamente');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error al descargar PDF');
    }
  };

  const handleDownloadAcceptance = async (registrationId, fullName, eventTitle) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/registrations/${registrationId}/acceptance-document`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const safeName = (fullName || 'participante').replace(/\s+/g, '_');
      const safeEvent = (eventTitle || 'evento').replace(/\s+/g, '_');
      link.setAttribute('download', `aceptacion_${safeName}_${safeEvent}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Documento de aceptación descargado');
    } catch (error) {
      console.error('Error downloading acceptance document:', error);
      toast.error('Error al descargar documento de aceptación');
    }
  };

  const canCancelRegistration = (registration) => {
    if (!registration.event) return false;

    const cancellationDays = registration.event.cancellation_days || 7;
    const eventDate = new Date(registration.event.start_date);
    const today = new Date();
    const daysUntilEvent = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));

    return daysUntilEvent >= cancellationDays;
  };

  const getDaysUntilEvent = (startDate) => {
    const eventDate = new Date(startDate);
    const today = new Date();
    const daysUntil = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));
    return daysUntil;
  };

  const fetchGallery = async (eventId, eventTitle) => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/gallery`);
      setGalleryPhotos(response.data.photos || []);
      setCurrentEventTitle(eventTitle);
      setShowGallery(true);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Solo los usuarios registrados pueden ver la galería');
      } else {
        toast.error('Error al cargar la galería');
      }
    }
  };

  const handleCancelClick = (registration) => {
    setSelectedRegistration(registration);
    setShowCancelDialog(true);
  };

  const handleCancelConfirm = async () => {
    if (!selectedRegistration) return;

    setCancelling(true);
    try {
      await axios.delete(`${API}/registrations/${selectedRegistration.id}`);
      
      toast.success('Inscripción cancelada exitosamente. Recibirás un email de confirmación.');
      
      // Refresh registrations list
      await fetchRegistrations();
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al cancelar la inscripción';
      toast.error(errorMessage);
    } finally {
      setCancelling(false);
      setShowCancelDialog(false);
      setSelectedRegistration(null);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Cargando...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-gray-100 py-12 px-4 glove-friendly">
      <div className="max-w-5xl mx-auto space-y-8">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-gray-800 bg-clip-text text-transparent" data-testid="my-registrations-title">
            Mis Inscripciones
          </h1>
          <p className="text-gray-600 mt-2">Eventos en los que estás inscrito</p>
        </div>

        {registrations.length === 0 ? (
          <Card className="text-center py-12" data-testid="no-registrations">
            <CardContent>
              <p className="text-gray-600 mb-4">No tienes inscripciones aún</p>
              <Button onClick={() => navigate('/')} data-testid="browse-events-btn">
                Explorar Eventos
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6" data-testid="registrations-list">
            {registrations
              .filter(reg => reg.event?.status !== 'archived')
              .sort((a, b) => {
                // Eventos activos/bloqueados: más próximos primero
                // Eventos completados: más recientes primero
                const statusA = a.event?.status;
                const statusB = b.event?.status;
                const dateA = new Date(a.event?.start_date);
                const dateB = new Date(b.event?.start_date);
                
                // Si ambos son completados, ordenar descendente (más reciente primero)
                if (statusA === 'completed' && statusB === 'completed') {
                  return dateB - dateA;
                }
                // Si ambos son activos/bloqueados, ordenar ascendente (más próximo primero)
                if (statusA !== 'completed' && statusB !== 'completed') {
                  return dateA - dateB;
                }
                // Eventos activos/bloqueados van primero que completados
                if (statusA !== 'completed' && statusB === 'completed') return -1;
                if (statusA === 'completed' && statusB !== 'completed') return 1;
                return 0;
              })
              .map((reg) => (
              <Card key={reg.id} className="border-0 shadow-lg" data-testid={`registration-card-${reg.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <CardTitle className="text-xl" data-testid={`registration-event-title-${reg.id}`}>{reg.event?.title || <span className="text-gray-500 italic">Evento eliminado por el organizador — puedes borrar esta inscripción</span>}</CardTitle>
                        {reg.event?.status === 'completed' && (
                          <Badge className="bg-gray-500 text-white">
                            REALIZADO
                          </Badge>
                        )}
                        <Button
                          onClick={() => navigate(`/events/${reg.event_id}`)}
                          variant="outline"
                          size="sm"
                          className="text-orange-600 border-orange-300 hover:bg-orange-50"
                          data-testid={`view-details-${reg.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Ver Detalles
                        </Button>
                        <Button
                          onClick={() => fetchGallery(reg.event_id, reg.event?.title)}
                          variant="outline"
                          size="sm"
                          className="text-purple-600 border-purple-300 hover:bg-purple-50"
                          data-testid={`view-gallery-${reg.id}`}
                        >
                          <ImageIcon className="w-4 h-4 mr-1" />
                          Galería
                        </Button>
                      </div>
                      <CardDescription className="flex items-center gap-4 mt-2">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {reg.event?.start_date && new Date(reg.event.start_date).toLocaleDateString('es-ES', { 
                            day: 'numeric', 
                            month: 'long', 
                            year: 'numeric' 
                          })}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {reg.event?.organizer || 'Sin organizador'}
                        </span>
                      </CardDescription>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Badge 
                        className={reg.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}
                        data-testid={`payment-status-${reg.id}`}
                      >
                        {reg.payment_status === 'paid' ? 'Pagado' : 'Pendiente'}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Precio Final</p>
                      <p className="font-semibold" data-testid={`final-price-${reg.id}`}>{reg.final_price}€</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Moto</p>
                      <p className="font-semibold" data-testid={`moto-info-${reg.id}`}>{reg.moto_brand} {reg.moto_model}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Matrícula</p>
                      <p className="font-semibold" data-testid={`moto-plate-${reg.id}`}>{reg.moto_plate}</p>
                    </div>
                    {reg.discount_applied && (
                      <div>
                        <p className="text-gray-600">Descuento</p>
                        <p className="font-semibold text-green-600" data-testid={`discount-${reg.id}`}>{reg.discount_applied.percentage}%</p>
                      </div>
                    )}
                  </div>

                  {/* Download buttons - GPX and PDF */}
                  {(reg.event?.gpx_file || (reg.payment_status === 'paid' && reg.event?.info_pdf)) && (
                    <div className="pt-4 border-t">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Archivos del evento:</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {reg.event?.gpx_file && (
                          <Button
                            onClick={() => handleDownloadGPX(reg.event_id, reg.event?.title)}
                            variant="outline"
                            className="w-full"
                            data-testid={`download-gpx-${reg.id}`}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Descargar GPX
                          </Button>
                        )}
                        {reg.payment_status === 'paid' && reg.event?.info_pdf && (
                          <Button
                            onClick={() => handleDownloadPDF(reg.event.info_pdf, reg.event?.title)}
                            variant="outline"
                            className="w-full"
                            data-testid={`download-pdf-${reg.id}`}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Descargar PDF Informativo
                          </Button>
                        )}
                        <Button
                          onClick={() => handleDownloadAcceptance(reg.id, reg.full_name, reg.event?.title)}
                          variant="outline"
                          className="w-full"
                          data-testid={`download-acceptance-${reg.id}`}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Descargar Documento de Aceptación
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Tracking Photos Section - Independent of downloads */}
                  {reg.event?.requires_tracking && reg.event?.event_mode === 'free' && (
                    <div className="pt-4 border-t space-y-3">
                      <FreeModeProgress registrationId={reg.id} eventId={reg.event_id} />
                      <Button
                        onClick={() => navigate(`/pasaporte/${reg.id}`)}
                        className="w-full h-14 text-base bg-violet-700 hover:bg-violet-800 text-white glove-friendly"
                        data-testid={`passport-map-btn-${reg.id}`}
                      >
                        <Stamp className="w-5 h-5 mr-2" />
                        Mapa Pasaporte · waypoints y fotos
                      </Button>
                    </div>
                  )}

                  {reg.event?.requires_tracking && (reg.event?.target_km > 0 || reg.tracking_stats?.total_km_traveled > 0) && (
                    <div className="pt-4 border-t" data-testid={`km-progress-${reg.id}`}>
                      <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-emerald-900 flex items-center gap-1">
                            <Navigation className="w-4 h-4" />
                            Kilómetros acumulados
                          </span>
                          <span className="text-lg font-bold text-emerald-700" data-testid={`km-traveled-${reg.id}`}>
                            {(reg.tracking_stats?.total_km_traveled || 0).toLocaleString('es-ES', { maximumFractionDigits: 0 })} km
                            {reg.event?.target_km > 0 && <span className="text-sm font-normal text-emerald-600"> / {Number(reg.event.target_km).toLocaleString('es-ES')} km</span>}
                          </span>
                        </div>
                        {reg.event?.target_km > 0 && (() => {
                          const pct = Math.min(100, Math.round(((reg.tracking_stats?.total_km_traveled || 0) / reg.event.target_km) * 100));
                          return (
                            <>
                              <div className="h-4 w-full rounded-full bg-emerald-100 overflow-hidden">
                                <div className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-emerald-600' : 'bg-gradient-to-r from-orange-400 to-emerald-500'}`} style={{ width: `${pct}%` }} data-testid={`km-progress-bar-${reg.id}`} />
                              </div>
                              <p className="text-xs text-emerald-700 mt-1">{pct}% del objetivo{pct >= 100 ? ' · ¡Objetivo conseguido!' : ''}</p>
                            </>
                          );
                        })()}
                        <p className="text-xs text-gray-500 mt-1">Suma de los km registrados por GPS (app, Traccar o Spotwalla) en este evento.</p>
                        {reg.event?.target_km > 0 && <KmRanking eventId={reg.event_id} />}
                      </div>
                    </div>
                  )}

                  {reg.event?.requires_tracking && (
                    <div className="pt-4 border-t">
                      {(() => {
                        const trackingInfo = getTrackingStatusInfo(reg.event);
                        return (
                          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-blue-900 flex items-center gap-1">
                                <Camera className="w-4 h-4" />
                                Seguimiento de Ruta
                              </span>
                              <Badge 
                                variant="outline" 
                                className={
                                  trackingInfo?.status === 'active' ? 'bg-green-100 text-green-700 border-green-300' :
                                  trackingInfo?.status === 'pending' ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
                                  'bg-gray-100 text-gray-600 border-gray-300'
                                }
                              >
                                {trackingInfo?.status === 'active' && <Clock className="w-3 h-3 mr-1" />}
                                {trackingInfo?.message}
                              </Badge>
                            </div>
                            <p className="text-xs text-blue-700 mb-3">
                              {reg.event?.event_mode === 'free'
                                ? 'Las fotos de los waypoints se suben desde el Mapa Pasaporte (una por waypoint, sin límite). Aquí solo fotos generales del viaje (máx. 20).'
                                : 'Sube fotos de los puntos de control para acreditar el cumplimiento de la ruta (una por waypoint; fotos generales máx. 20)'}
                            </p>
                            <Button
                              onClick={() => openTrackingModal(reg)}
                              variant="outline"
                              className="w-full border-blue-300 text-blue-700 hover:bg-blue-100"
                              data-testid={`tracking-upload-btn-${reg.id}`}
                            >
                              <Upload className="w-4 h-4 mr-2" />
                              {reg.event?.event_mode === 'free' ? 'Fotos generales del viaje' : 'WP de Ruta'}
                            </Button>
                            
                            {/* Route Tracker Button */}
                            {(reg.event?.gpx_file || reg.event?.track1_file || reg.event?.track2_file || reg.event?.track3_file) && (
                              <Button
                                onClick={() => navigate(`/route-tracker/${reg.id}`)}
                                className="w-full mt-2 bg-green-600 hover:bg-green-700 text-white"
                                data-testid={`route-tracker-btn-${reg.id}`}
                              >
                                <Navigation className="w-4 h-4 mr-2" />
                                Seguir Ruta con GPS
                              </Button>
                            )}

                            {reg.event?.requires_tracking && (
                              <Button
                                onClick={() => navigate(`/beacon/${reg.id}`)}
                                className="w-full mt-2 bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-700"
                                data-testid={`beacon-mode-btn-${reg.id}`}
                              >
                                <Radio className="w-4 h-4 mr-2 text-orange-500" />
                                Modo Baliza (sin mapa)
                              </Button>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Cancellation Section - ALWAYS SHOW BUTTON (except for completed events) */}
                  <div className="pt-4 border-t">
                    {reg.event?.status === 'completed' ? (
                      <div className="p-4 bg-gray-50 rounded-lg text-center">
                        <p className="text-sm text-gray-700">
                          <strong>✓ Evento Realizado</strong>
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          Este evento ya finalizó. Esperamos que hayas disfrutado de la experiencia.
                        </p>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-sm text-gray-600 mb-1">
                            <strong>Política de cancelación:</strong> Puedes cancelar hasta {reg.event?.cancellation_days || 7} días antes del evento.
                          </p>
                          {canCancelRegistration(reg) ? (
                            <p className="text-sm text-green-600">
                              ✓ Quedan {getDaysUntilEvent(reg.event?.start_date)} días. Puedes cancelar tu inscripción.
                            </p>
                          ) : (
                            <p className="text-sm text-orange-600">
                              ⚠️ Quedan {Math.abs(getDaysUntilEvent(reg.event?.start_date))} días. El plazo de cancelación puede haber expirado.
                            </p>
                          )}
                        </div>
                        <Button
                          onClick={() => handleCancelClick(reg)}
                          variant="destructive"
                          className="bg-red-500 hover:bg-red-600"
                          data-testid={`cancel-registration-${reg.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Cancelar Inscripción
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Cancel Confirmation Dialog */}
      {showCancelDialog && selectedRegistration && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Confirmar Cancelación
            </h3>
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de que deseas cancelar tu inscripción para{' '}
              <strong>{selectedRegistration.event?.title}</strong>?
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Recibirás un email de confirmación y tu plaza quedará disponible para otros participantes.
            </p>
            <div className="flex space-x-4">
              <Button
                onClick={() => {
                  setShowCancelDialog(false);
                  setSelectedRegistration(null);
                }}
                disabled={cancelling}
                variant="outline"
                className="flex-1"
              >
                No, mantener inscripción
              </Button>
              <Button
                onClick={handleCancelConfirm}
                disabled={cancelling}
                variant="destructive"
                className="flex-1 bg-red-500 hover:bg-red-600"
              >
                {cancelling ? 'Cancelando...' : 'Sí, cancelar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Gallery Dialog */}
      <Dialog open={showGallery} onOpenChange={setShowGallery}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="w-6 h-6 text-purple-600" />
              Galería de Fotos - {currentEventTitle}
            </DialogTitle>
            <DialogDescription>
              {galleryPhotos.length > 0 
                ? `${galleryPhotos.length} foto(s) disponible(s)` 
                : 'Aún no hay fotos disponibles'}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {galleryPhotos.length === 0 ? (
              <div className="text-center py-12">
                <ImageIcon className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600">No hay fotos en la galería todavía</p>
                <p className="text-sm text-gray-500 mt-2">El organizador subirá fotos pronto</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {galleryPhotos.map((photoUrl, index) => (
                  <div key={photoUrl} className="group relative">
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${photoUrl}`}
                      alt={`Foto ${index + 1}`}
                      className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-purple-500 transition-all cursor-pointer"
                      onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${photoUrl}`, '_blank')}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center">
                      <Download 
                        className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = `${process.env.REACT_APP_BACKEND_URL}${photoUrl}`;
                          link.download = `foto-${index + 1}.jpg`;
                          link.click();
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Tracking Photos Modal */}
      <Dialog open={showTrackingModal} onOpenChange={setShowTrackingModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-blue-600" />
              Seguimiento de Ruta
            </DialogTitle>
            <DialogDescription>
              {trackingRegistration?.event?.title}
            </DialogDescription>
          </DialogHeader>

          <Tabs key={trackingRegistration?.id} defaultValue={trackingRegistration?.event?.event_mode === 'free' ? 'photos' : 'waypoints'} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="waypoints">📍 Waypoints</TabsTrigger>
              <TabsTrigger value="photos">📸 Fotos Generales</TabsTrigger>
            </TabsList>

            {/* Waypoints Tab */}
            <TabsContent value="waypoints" className="mt-4">
              {trackingRegistration && (
                <WaypointTracker
                  registrationId={trackingRegistration.id}
                  eventId={trackingRegistration.event_id}
                  canUpload={canUploadTrackingPhotos(trackingRegistration.event)}
                />
              )}
            </TabsContent>

            {/* General Photos Tab */}
            <TabsContent value="photos" className="mt-4">
              <div className="space-y-4">
                {/* Upload Section */}
                {trackingRegistration && canUploadTrackingPhotos(trackingRegistration.event) && trackingPhotos.filter(p => !p.waypoint_id).length < 20 && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-medium text-blue-900 mb-3">Subir foto general</h4>
                    <p className="text-sm text-blue-700 mb-3">
                      Estas fotos no están asociadas a ningún waypoint específico
                    </p>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="trackingPhoto">Foto *</Label>
                        <Input
                          id="trackingPhoto"
                          type="file"
                          accept="image/*"
                          onChange={(e) => setNewPhotoFile(e.target.files[0])}
                          className="cursor-pointer"
                        />
                      </div>
                      <div>
                        <Label htmlFor="trackingDescription">Descripción (opcional)</Label>
                        <Textarea
                          id="trackingDescription"
                          value={newPhotoDescription}
                          onChange={(e) => setNewPhotoDescription(e.target.value)}
                          placeholder="Ej: Vista panorámica del recorrido"
                          rows={2}
                        />
                      </div>
                      <Button
                        onClick={handleUploadTrackingPhoto}
                        disabled={!newPhotoFile || uploadingPhoto}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                      >
                        {uploadingPhoto ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                            Subiendo...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Subir Foto
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Photos count info */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">
                    {trackingPhotos.filter(p => !p.waypoint_id).length} fotos generales subidas
                  </span>
                </div>

                {/* Photos Grid - only show non-waypoint photos */}
                {loadingTrackingPhotos ? (
                  <div className="text-center py-8">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-gray-600">Cargando fotos...</p>
                  </div>
                ) : trackingPhotos.filter(p => !p.waypoint_id).length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <Camera className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600">No has subido fotos generales todavía</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Usa la pestaña «Waypoints» para subir fotos de los puntos de control
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {trackingPhotos.filter(p => !p.waypoint_id).map((photo) => (
                      <div key={photo.id} className="relative group">
                        <img
                          src={photo.photo_url}
                          alt={photo.description || 'Foto de seguimiento'}
                          className="w-full h-32 object-cover rounded-lg border"
                        />
                        {/* Status badge */}
                        <div className="absolute top-2 left-2">
                          <Badge
                            variant="outline"
                            className={
                              photo.status === 'verified' ? 'bg-green-500 text-white border-green-500' :
                              photo.status === 'rejected' ? 'bg-red-500 text-white border-red-500' :
                              'bg-yellow-500 text-white border-yellow-500'
                            }
                          >
                            {photo.status === 'verified' && <CheckCircle className="w-3 h-3 mr-1" />}
                            {photo.status === 'rejected' && <XCircle className="w-3 h-3 mr-1" />}
                            {photo.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                            {photo.status === 'verified' ? 'Verificada' : photo.status === 'rejected' ? 'Rechazada' : 'Pendiente'}
                          </Badge>
                        </div>
                        {/* Delete button */}
                        {canUploadTrackingPhotos(trackingRegistration?.event) && (
                          <Button
                            size="sm"
                            variant="destructive"
                            className="absolute top-2 right-2 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleDeleteTrackingPhoto(photo.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                        {/* Description */}
                        {photo.description && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs p-2 rounded-b-lg">
                            {photo.description}
                          </div>
                        )}
                        {/* View full */}
                        <div 
                          className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center cursor-pointer"
                          onClick={() => window.open(photo.photo_url, '_blank')}
                        >
                          <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
